/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from "@emotion/react";
import * as React from "react";
import { Input } from "./Input";
import {
  FORMAT_REGEX,
  formatDateToTime,
  normalizeTimeInput,
  parseTimeToDate,
  TimeFormat,
} from "./timeInputUtils";


const rootBaseCss = css`
  display: inline-flex;
  flex: 0 0 auto; // prevent flexbox resizing
`;

const inputVisualCss = css`
  /* Default (looks like 1px – matches base Input) */
  box-shadow: inset 0 0 0 0 transparent;

  &:focus {
    /* Looks like 2px */
    box-shadow: inset 0 0 0 1px #6b9cff;
  }
`;


export interface TimeInputProps {
  id?: string;
  title?: string;
  disabled?: boolean;
  value: Date | null;
  onChange: (value: Date | null) => void;
  format: TimeFormat;
  placeholder?: string;
  rootCss?: SerializedStyles; // CSS applied to root wrapper
  inputCss?: SerializedStyles;
  className?: string;
  onEnter?: () => void;
  onBlur?: () => void;  // Called after internal blur logic completes 
}

export function TimeInput({
  id,
  title,
  disabled = false,
  value,
  onChange,
  format,
  placeholder,
  rootCss,
  inputCss,
  className,
  onEnter,
  onBlur,
}: TimeInputProps) {
  const [text, setText] = React.useState(
    value ? formatDateToTime(value, format) : "",
  );

  // Sync when value or format changes externally
  React.useEffect(() => {
    if (value) {
      setText(formatDateToTime(value, format));
    } else {
      setText("");
    }
  }, [value, format]);

  const allowedCharRegex = React.useMemo(
    () =>
      format.includes("a") || format.includes("A")
        ? /^[0-9: apmAPM]*$/
        : /^[0-9:]*$/,
    [format],
  );

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;

      const raw = e.target.value;

      if (!allowedCharRegex.test(raw)) {
        return;
      }

      const next = format.includes("A")
        ? raw.toUpperCase()
        : format.includes("a")
          ? raw.toLowerCase()
          : raw;

      setText(next);

      // Clear input - return null
      if (next === "") {
        onChange(null);
        return;
      }

      if (FORMAT_REGEX[format].test(next)) {
        const baseDate = value ?? new Date();
        onChange(parseTimeToDate(next, format, baseDate));
      }
    },
    [allowedCharRegex, disabled, format, onChange, value],
  );

  const commitValue = React.useCallback(() => {
    if (disabled || !text) {
      onBlur?.();
      return;
    }

    const referenceValue = value;
    const normalized = normalizeTimeInput(text, {format, referenceValue});

    if (FORMAT_REGEX[format].test(normalized)) {
      const baseDate = value ?? new Date();
      const parsed = parseTimeToDate(normalized, format, baseDate);

      if (parsed) {
        setText(formatDateToTime(parsed, format));
        onChange(parsed);
      }
    } else {
      setText(value ? formatDateToTime(value, format) : "");
    }

    onBlur?.();
  }, [disabled, text, format, value, onBlur, onChange]);


  const handleBlur = React.useCallback(() => {
    commitValue();
  }, [commitValue]);


  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Enter") return;

      e.preventDefault();
      commitValue();
      onEnter?.();
    },
    [commitValue, onEnter],
  );

  return (
    <div css={css`${rootBaseCss};${rootCss}`}>
      <Input
        id={id}
        title={title}
        type="text"
        className={className}
        css={css`${inputVisualCss};${inputCss}`}
        value={text}
        disabled={disabled}
        placeholder={placeholder ?? format}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        spellCheck={false}
      />
    </div>
  );
}
